import { trimLanguageTag } from 'helpers/commonHelper';
import Cookies, { CookieAttributes } from 'js-cookie';
import React, { useState } from 'react';
import { injectIntl, IntlProvider } from 'react-intl';
import messagesEn from 'translations/en.json';
import messagesPl from 'translations/pl.json';
import messagesUk from 'translations/uk.json';
import Locale from 'types/Locale';

import LocaleContextProvider from './LocaleContextProvider';
import { LocaleProviderProps } from './LocaleProvider.types';

const WrappedLocaleContext = injectIntl(LocaleContextProvider);

const cookiesLocale = Cookies.get('locale');

const browserLanguage = navigator.language;

const availableMessages = {
  en: messagesEn,
  pl: messagesPl,
  uk: messagesUk,
};

const getLocale = () =>
  cookiesLocale ||
  (Object.prototype.hasOwnProperty.call(availableMessages, trimLanguageTag(browserLanguage))
    ? browserLanguage
    : Locale.ENGLISH);

function LocaleProvider(props: LocaleProviderProps) {
  const [locale, setLocaleState] = useState(getLocale);
  const { children } = props;
  const messages = availableMessages[trimLanguageTag(locale)];

  const setLocale = (newLocale: string) => {
    document.documentElement.lang = newLocale;
    setLocaleState(newLocale);
  };

  const setLocaleCookie = (newLocale: string) => {
    Cookies.set('locale', newLocale, {
      SameSite: 'lax',
      expires: 30,
      secure: window.location.protocol === 'https:',
    } as CookieAttributes);
  };

  const setLocaleFromPaymentRequest = (requestLocale: string | null) => {
    const currentCookiesLocale = Cookies.get('locale');
    let localeToSetup = '';
    if (requestLocale === null && (currentCookiesLocale === null || currentCookiesLocale === undefined)) {
      setLocale(Locale.POLISH);
    } else if (requestLocale && !currentCookiesLocale) {
      localeToSetup = Object.prototype.hasOwnProperty.call(availableMessages, trimLanguageTag(requestLocale))
        ? requestLocale
        : Locale.ENGLISH;
      setLocale(localeToSetup);
    }
  };

  const setLocaleSelectedByUser = (selectedLocale: string) => {
    setLocale(selectedLocale);
    setLocaleCookie(selectedLocale);
  };

  return (
    <IntlProvider locale={locale} messages={messages}>
      <WrappedLocaleContext
        setLocaleSelectedByUser={setLocaleSelectedByUser}
        setLocaleFromPaymentRequest={setLocaleFromPaymentRequest}
        locale={locale}
      >
        {children}
      </WrappedLocaleContext>
    </IntlProvider>
  );
}

export default LocaleProvider;
